import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { type AvatarShape, isValidAvatarShape, getAvatarMaskUrl } from '@/lib/avatarShape';

/**
 * Lighten a hex color by mixing it with white.
 * factor 0 = original, 1 = white.
 */
function lightenHex(hex: string, factor: number): string {
  const h = hex.replace('#', '');
  const r = Math.round(parseInt(h.substring(0, 2), 16) + (255 - parseInt(h.substring(0, 2), 16)) * factor);
  const g = Math.round(parseInt(h.substring(2, 4), 16) + (255 - parseInt(h.substring(2, 4), 16)) * factor);
  const b = Math.round(parseInt(h.substring(4, 6), 16) + (255 - parseInt(h.substring(4, 6), 16)) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

interface IridescentAvatarProps {
  src?: string;
  alt?: string;
  size?: number;
  className?: string;
  /** Emoji avatar shape from kind-0 metadata `shape` field */
  shape?: AvatarShape;
  /** Theme color hex (e.g. "#1a1a2e"). When set, the ring uses this color instead of the default pastel rainbow. */
  themeColor?: string;
}

export function IridescentAvatar({ src, alt = 'User', size = 72, className, shape, themeColor }: IridescentAvatarProps) {
  const hasShape = !!shape && isValidAvatarShape(shape);

  // Generate the emoji mask URL synchronously (getAvatarMaskUrl is sync + cached)
  const maskUrl = useMemo(() => {
    if (hasShape && shape) return getAvatarMaskUrl(shape);
    return '';
  }, [hasShape, shape]);

  const maskStyle = useMemo<React.CSSProperties>(() => {
    if (!maskUrl) return {};
    return {
      WebkitMaskImage: `url(${maskUrl})`,
      maskImage: `url(${maskUrl})`,
      WebkitMaskSize: 'contain',
      maskSize: 'contain' as string,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat' as string,
      WebkitMaskPosition: 'center',
      maskPosition: 'center' as string,
    };
  }, [maskUrl]);

  // Fully opaque ring gradient — no alpha/transparency
  const ringBackground = themeColor
    ? `conic-gradient(from 0deg, ${themeColor}, ${lightenHex(themeColor, 0.35)}, ${themeColor}, ${lightenHex(themeColor, 0.35)}, ${themeColor})`
    : 'conic-gradient(from 0deg, #E8A0BF, #C8A2D4, #A8D0E8, #A8E0D0, #E8A0BF)';

  // Fully opaque sparkle colors derived from theme
  const sparkleColors = themeColor
    ? [themeColor, lightenHex(themeColor, 0.25), lightenHex(themeColor, 0.45), lightenHex(themeColor, 0.15)]
    : undefined;

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      {/* Ring — emoji mask shape or square default */}
      <div
        className="p-[4px]"
        style={{
          width: size + 8,
          height: size + 8,
          background: ringBackground,
          ...(hasShape ? maskStyle : {}),
        }}
      >
        <div
          className="overflow-hidden bg-cream"
          style={{ width: size, height: size, ...(hasShape ? maskStyle : {}) }}
        >
          {src ? (
            <img src={src} alt={alt} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pol-pink to-pol-lilac" />
          )}
        </div>
      </div>

      {/* Decorative sparkle stars — fully opaque, tinted to theme when available */}
      <span className="absolute -top-1 -right-1 text-xs animate-doodle-float" style={{ color: sparkleColors?.[0] ?? '#C8A2D4' }}>✦</span>
      <span className="absolute -bottom-0.5 -left-1.5 text-[10px] animate-doodle-float" style={{ color: sparkleColors?.[1] ?? '#E8A0BF', animationDelay: '0.8s' }}>✦</span>
      <span className="absolute top-1 -left-2 text-[8px] animate-doodle-float" style={{ color: sparkleColors?.[2] ?? '#A8D0E8', animationDelay: '1.6s' }}>✦</span>
      <span className="absolute -bottom-1 right-0 text-[10px] animate-doodle-float" style={{ color: sparkleColors?.[3] ?? '#E8C888', animationDelay: '2.2s' }}>✦</span>
    </div>
  );
}
